<template>
    <div class="form-group">
        <div class="input-icon" :class="{ 'form-floating': showLabel }">
            <input
                type="text" 
                ref="txtNumberInput"
                :id="`input-${id}`"
                :value="displayValue"
                :readonly="isReadOnly" 
                @input="updateModel" 
                @keypress="onlyNumeric" 
                @focus="hasFocus = true"
                @blur="hasFocus = false"
                @keyup.enter="emit('entered')"
                class="form-control"
            />

            <i>&pound;</i>

            <validation-label
                v-show="showLabel"
                ref="label"
                :elementId="`input-${id}`"
                :text="description"
                :abbreviatedText="abbreviatedDescription"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, PropType, watchEffect } from 'vue';
import ValidationLabel from '@/shared/components/ValidationLabel.vue';
import { generateUUID } from '@/guiide/ts/utilities';

const props = defineProps({
    modelValue: {
        type: [Number, null] as PropType<number | null>,
        default: null
    },
    description: { type: String, default: "" },
    abbreviatedDescription: { type: String, default: "" },
    max: { type: Number, default: 100 },
    min: { type: Number, default: 1 },
    optional: { type: Boolean, default: false },
    isReadOnly: { type: Boolean, default: false },
    showLabel: { type: Boolean, default: true }
});

const emit = defineEmits(['update:modelValue', 'entered']);

const id = generateUUID();
const txtNumberInput = ref(null);
const label = ref(null);
const displayValue = ref(props.modelValue?.toString() ?? '');
const hasFocus = ref(false);

const updateModel = (event: Event) => {
    const value = (event.target as HTMLInputElement).value;

    if (value === "") {
        label.value.hideAnyWarnings();
        emit('update:modelValue', null);
        return;
    }

    if (!/^\d+$/.test(value)) {
        return;
    }

    const numberValue = parseInt(value, 10);

    isValidInternal(numberValue);

    if (isNaN(numberValue)) {
        emit('update:modelValue', null);
    } else {
        emit('update:modelValue', numberValue);
    }
};

watchEffect(() => {
    if (props.modelValue === null) {
        displayValue.value = "";
        return;
    }

    if (hasFocus.value) {
        displayValue.value = props.modelValue.toString();
    } else {
        displayValue.value = props.modelValue.toLocaleString();
    }
});

const onlyNumeric = (event: KeyboardEvent) => {
    const keyValue = event.key;
    if (!keyValue.match(/[0-9]/)) {
        event.preventDefault();
    }
};

const isValid = (): boolean => {
    return isValidInternal(props.modelValue);
}

function isValidInternal(numberToValidate: number | null): boolean {
    if (numberToValidate === null) {
        if (!props.optional) {
            label.value.warn("required");
            return false;
        }
        return true;
    }
    
    if (numberToValidate! > props.max) {
        const maxFormatted = props.max.toLocaleString();
        label.value.warn(`<= ${maxFormatted}`);
        return false;
    } else if (numberToValidate! < props.min) {
        const minFormatted = props.min.toLocaleString();
        label.value.warn(`>= ${minFormatted}`);
        return false;
    }

    label.value.hideAnyWarnings();
    return true;
}

const hideAnyWarnings = () => {
    label.value.hideAnyWarnings();
}

function takeFocus() {
    txtNumberInput.value.focus();
}

defineExpose({ isValid, hideAnyWarnings, takeFocus });

</script>

<style scoped>
.input-icon {
  position: relative;
}

.input-icon > i {
  position: absolute;
  display: block;
  top: 50%;
  transform: translateY(-50%);
  left: 7px;
  pointer-events: none;
  width: 25px;
  text-align: center;
  font-style: normal;
}

.input-icon.form-floating > i {
  transform: translate(0, -22%);
  top: 53%;
}

.input-icon > input[type="text"]:focus + i {
  display: block;
}

.input-icon > input {
  padding-left: 27px;
  padding-right: 0;
}

.form-floating > label {
  background-color: white;
  height: 96%;
  width: 99%;
}

.form-floating > .form-control:focus ~ label,
.form-floating > .form-control:not(:placeholder-shown) ~ label,
.form-floating > .form-select ~ label {
  background-color: transparent;
  height: 96%;
}

.form-floating > label {
  top: 1px;
  left: 1px;
  padding: 0.8rem 0.75rem;
  border-radius: 4px;
}
</style>
